import { coreDefaultUiColors } from "@b2bportal/core-ui";
import type { ThemeOptions } from "@material-ui/core/styles";

const tenantUiColors = {
  "tenant-color__skeleton-background": "#e0e0e0",
  "tenant-color__skeleton-loader": "rgba(255, 255, 255, 0.4)",
};

export const themeVariables = {
  /** We have to hardcode these variables because they match the tokens.css variables so we can't reference them*/
  primary: "var(--color-primary)",
  secondary: "var(--color-secondary)",
  white: "#FFF",

  "text-secondary": "var(--color-text-secondary)",
  "page-background-color": "var(--background)",
  "background-color": "var(--surface)",
  "popup-background-color": "var(--surface)",
  "divider-color-300": "var(--border)",
  "divider-color-200": "var(--muted)",
  "divider-color-100": "var(--secondary)",

  "disabled-background-color": "var(--muted)",

  /** Other configurations can point to an existing variable **/
  "homepage-title-primary": "var(--primary)",

  /** Example of how to override the font family **/
  "font-family": ["Proxima Nova", "sans-serif"].join(","),

  ////////

  /** Common */
  "text-primary": "var(--foreground)",
  "text-primary-inverse": "var(--white)",
  "text-subdued": "var(--muted-foreground)",
  "text-disabled": "var(--muted-foreground)",
  "text-muted": "var(--color-muted)",

  "link-primary": "var(--text-link)",

  /** Button */
  "button-primary": "var(--primary-foreground)",
  "button-primary-padding": "var(--button-padding)",
  "button-primary-border-radius": "40px",
  "button-primary-background": "var(--button-primary-background-color)",
  "button-primary-hover": "var(--primary-foreground)",
  "button-primary-disabled": "var(--white)",
  "button-primary-disabled-background":
    "var(--button-primary-disabled-background-color)",
  "button-primary-border": "1px solid var(--button-primary-background)",
  "button-primary-border-disabled":
    "1px solid var(--button-primary-disabled-border)",
  "button-secondary": "var(--primary)",
  "button-secondary-background": "var(--white)",
  "button-secondary-border": "1px solid var(--primary)",
  "button-secondary-border-disabled":
    "1px solid var(--button-primary-disabled-background)",
  "button-tertiary": "var(--text-primary)",
  "button-tertiary-background": "var(--surface)",
  "button-tertiary-hover-background": "var(--surface)",
  "button-tertiary-hover": "var(--text-primary)",
  "button-tertiary-hover-border": "var(--primary)",
  "button-tertiary-disabled": "var(--surface)",
  "button-tertiary-border": "var(--border)",
  "button-tertiary-padding": "0.5rem 1rem",
  "button-tertiary-disabled-background": "var(--muted)",

  // Radii
  "border-radius-sm": "var(--radius-sm)",
  "border-radius-md": "var(--radius-md)",
  "border-radius-lg": "var(--radius-lg)",
  "border-radius-xl": "var(--radius-xl)",
  "border-radius-xxl": "var(--radius-3xl)",

  // Modal
  "modal-border-radius-desktop": "var(--border-radius-xl)",
  "modal-title-color-desktop": "var(--text-primary)",
  "modal-subtitle-color-desktop": "var(--text-secondary)",

  // Dividers
  "divider-border-dark": "1px solid var(--divider-color-300)",
  "divider-border-light": "1px solid var(--divider-color-100)",

  // RadioSelectRow
  "radio-select-border-color": "var(--divider-color-200)",
  "radio-select-active-border-color": "var(--black)",
  "radio-select-border-radius": "var(--border-radius-xl)",
  "radio-select-disabled-border-color": "var(--text-disabled)",

  "radio-select-border": "2px solid var(--primary)",

  /** Loading */
  "loading-icon-track-color": "transparent",
  "loading-icon-border-color": "var(--primary)",
  "loading-background-color": "var(--secondary)",
  "loading-text-color": "var(--surface)",

  /** ========= UI ======== */
  "slider-background-color": "var(--muted)",
  "slider-range-background-color": "var(--primary)",
  "slider-thumb-background-color": "var(--primary)",
  "slider-thumb-value-background-color": "var(--primary)",
  "slider-thumb-value-color": "var(--primary-foreground)",

  "page-header-background-color": "var(--surface)",
  "page-header-text-color": "var(--text-primary)",
  "page-footer-background-color": "var(--surface)",
  "page-footer-text-color": "var(--text-primary)",

  "checkbox-border-color": "var(--checkbox-border)",
  "checkbox-checked-border-color": "var(--checkbox-checked-border)",
  "checkbox-checked-background-color": "var(--checkbox-checked-fill)",
  "checkbox-checked-icon-color": "var(--primary-foreground)",
  "checkbox-checked-disabled-background-color": "var(--muted-foreground)",
  "checkbox-disabled-background-color": "var(--muted-foreground)",
  "switch-checked-color": "var(--switch-checked-outter-color)",

  "input-background-color": "var(--surface)",
  "input-border-color": "var(--border)",
  "input-border": "1px solid var(--input-border-color)",
  "input-border-radius": "var(--radius)",
  "input-error-color": "var(--destructive)",
  "input-error-border-color": "var(--destructive)",
  "input-disabled-background-color": "var(--disabled-background-color)",
  "input-disabled-label-color": "var(--muted-foreground)",
  "input-disabled-text-color": "var(--foreground)",

  "notification-banner-text-color": "var(--surface)",

  "location-autocomplete-icon-background-color": "var(--secondary)",
  "location-autocomplete-icon-color": "var(--muted-foreground)",

  "map-marker-text": "var(--text-primary)",
  "map-marker-surface": "var(--background-color)",
  "map-marker-hover-text": "var(--primary-foreground)",
  "map-marker-hover-surface": "var(--primary)",

  "card-border-radius": "var(--border-radius-xxl)",
  "card-background": "var(--white)",
  "card-border": "1px solid var(--divider-color-100)",

  // TODO remove
  "border-radius": "var(--radius-2xl)",

  // CALENDAR COLORS
  "calendar-labels-background-color": "var(--muted)",
  "date-selected-text-color": "var(--text-primary)",
  "date-hover-text-color": "var(--text-primary)",
  "bucket-0-text-color": "var(--text-primary)",
  "bucket-1-text-color": "var(--text-primary)",
  "bucket-2-text-color": "var(--text-primary)",
  "bucket-3-text-color": "var(--text-primary)",
  "bucket-0-color": "var(--success)",
  "bucket-1-color": "var(--warning)",
  "bucket-2-color": "var(--warning)",
  "bucket-3-color": "var(--destructive)",
  "white-font-color": "var(--surface)",
  "grey-font-color": "var(--muted-foreground)",
  "date-hover-color": "var(--calendar-item-selected-background)",
  "date-range-limit-color": "var(--primary)",
  "date-selected-color": "var(--calendar-item-selected-background)",
  "date-disabled-color": "var(--muted)",

  // TRIPS
  "trips-breadcrumbs-color-text": "var(--surface)",
  "trips-heading-color-text": "var(--surface)",

  // Design System Colors
  // See https://www.figma.com/design/TEZAmVN4ajUtUApDXbdsEg/Token?node-id=3-2872&node-type=FRAME&t=fDu6lQijkbwDgRFh-0

  // Surface
  "surface-background": "var(--muted)",
  "success-fill-color": "var(--success)",
  "error-fill-color": "var(--destructive)",
  "caution-fill-color": "var(--warning)",
  "neutral-fill-color": "var(--muted)",
  "text-inverted-color": "var(--surface)",

  "success-tick-color": "var(--success)",
  ...coreDefaultUiColors,
  ...tenantUiColors,
};

// Minimal MUI theme configuration containing only component props that cannot be replicated in CSS
export const createMUIBaseline = (): ThemeOptions => ({
  props: {
    MuiTextField: {
      variant: "filled",
      fullWidth: true,
    },
    MuiSelect: {
      variant: "filled",
      fullWidth: true,
    },
    MuiFormControl: {
      fullWidth: true,
    },
    MuiFilledInput: {
      disableUnderline: true,
      fullWidth: true,
    },
    MuiPaper: {
      elevation: 0,
    },
    MuiRadio: {
      color: "primary",
      size: "small",
    },
    MuiCard: {
      variant: "outlined",
    },
    MuiButton: {
      variant: "contained",
    },
  },
});

export const muiBaseline = createMUIBaseline();
